// File: frontend/src/api/profile.ts

import { ApiError } from './auth'; // For consistent error handling
import { createApiClient, isAxiosError } from './client';

// --- Configuration ---
// Ensure this matches your other API files
import { API_BASE_URL } from '@/config/apiConfig';
// const API_BASE_URL = 'http://localhost:8000/api/v1'; // For local testing
console.log("Using API Base URL for Profile:", API_BASE_URL);

// --- Interfaces ---

// Corresponds to backend schema: schemas.contest_result.ContestResultReadWithContestInfo
export interface ApiContestHistoryEntry {
  id: number;
  user_id: number;
  contest_id: number;
  lineup_id: number | null;
  final_score: number;
  final_rank: number;
  prize_won: number | null;
  recorded_at: string; // ISO datetime string
  contest_name: string | null;
  contest_date: string | null; // ISO date string (YYYY-MM-DD)
  is_weekend_contest: boolean | null;
}

// Interface for paginated response (if your backend implements it this way,
// otherwise it might just be ApiContestHistoryEntry[])
// For now, our backend endpoint returns List[ContestResultReadWithContestInfo] directly.
// If you add full pagination (total_pages, total_items etc.) to backend, update this.
// export interface PaginatedContestHistory {
//   items: ApiContestHistoryEntry[];
//   page: number;
//   limit: number;
//   total_items: number;
//   total_pages: number;
// }


// --- Axios Client Setup ---
const profileApiClient = createApiClient({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Push notification preferences (NOTIFICATIONS_SPEC.md) ---
export interface NotificationPrefs {
  master: boolean;
  transactions: boolean;
  lineups: boolean;
  dfs: boolean;
  draft: boolean;
  starters: boolean; // opt-in, default OFF
}

export const fetchNotificationPrefs = async (token: string): Promise<NotificationPrefs> => {
  if (!token) throw new ApiError('Authentication required.', 401);
  const response = await profileApiClient.get<NotificationPrefs>(
    `${API_BASE_URL}/profile/me/notification-prefs`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const updateNotificationPrefs = async (
  token: string,
  patch: Partial<NotificationPrefs>
): Promise<NotificationPrefs> => {
  if (!token) throw new ApiError('Authentication required.', 401);
  const response = await profileApiClient.patch<NotificationPrefs>(
    `${API_BASE_URL}/profile/me/notification-prefs`,
    patch,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

// --- API Function to Fetch User's Contest History ---
export const fetchMyContestHistory = async (
  token: string,
  skip: number = 0,
  limit: number = 10
): Promise<ApiContestHistoryEntry[]> => {
  if (!token) {
    throw new ApiError("Authentication token is required to fetch contest history.", 401);
  }

  const url = `${API_BASE_URL}/profile/me/contest-history`;
  console.log(`[Profile API] Fetching contest history: ${url}?skip=${skip}&limit=${limit}`);

  try {
    const response = await profileApiClient.get<ApiContestHistoryEntry[]>(url, {
      headers: { Authorization: `Bearer ${token}` },
      params: { skip, limit }, // Send skip and limit as query parameters
    });
    console.log(`[Profile API] Received ${response.data.length} contest history entries.`);
    return response.data;
  } catch (error: any) {
    let errorMessage = `Failed to fetch your contest history.`;
    let status: number | undefined;

    if (isAxiosError(error)) {
      status = error.response?.status;
      console.error(`[Profile API] Axios error fetching contest history:`, {
        message: error.message, url: error.config?.url, status, data: error.response?.data,
      });
      if (status === 401 || status === 403) {
        errorMessage = 'Authentication/Authorization failed. Please log in again.';
      } else if (status === 404) {
        errorMessage = 'Contest history not found or endpoint unavailable.';
      } else {
        const backendError = error.response?.data?.detail;
        if (typeof backendError === 'string') {
          errorMessage = backendError;
        }
      }
    } else {
      console.error(`[Profile API] Unexpected error fetching contest history:`, error);
    }
    throw new ApiError(errorMessage, status);
  }
};

// --- (Optional) API Function for User Profile Summary ---
// export const fetchMyProfileSummary = async (token: string): Promise<ApiUserProfileSummary> => { ... }
